/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { get } from 'lodash';

// Components
import DropdownMenu from 'shared/ui/components/DropdownMenu.vue';
import SessionsDetails from 'ui/components/organizations/participants/SessionsDetails.vue';

// Mixins
import sessions from 'ui/mixins/organizations/sessions';

/**
 * @typedef {Object} BrowserSession
 *
 * @property {String} rootNodeUrl the event instance server base URL
 * @property {String} eid the event ID
 * @property {String} pid the user's participant ID on the event
 */

/**
 * This component is used for managing participant sessions on specific events.
 *
 * @vue-prop {String} email the email of the user
 * @vue-prop {Object} organization the organization details
 * @vue-prop {Number} refresher the probe used to refresh this very component from the parent
 *
 * @category ui.components.organizations.participants
 * @exports Sessions
 * @component
 */
const __vue_script__ = {
    name: 'Sessions',

    components: { DropdownMenu },

    mixins: [sessions],

    props: {
        email: {
            type: String,
            default: ''
        },

        organization: {
            type: Object,
            required: true
        },

        refresher: {
            type: Number,
            default: null
        }
    },

    data() {
        return {
            error: null,
            loading: false,
            workspaces: [],
            showNames: this.organization.allow_non_unique_email
        };
    },

    watch: {
        refresher() {
            this.init();
        },

        email: {
            immediate: true,
            handler() {
                this.init();
            }
        }
    },

    methods: {
        /**
         * Initializes the component.
         */
        async init() {
            try {
                this.loading = true;
                const result = await this.$services.userSession.getAll(this.organization._id, this.email);

                if (!result.ok) {
                    this.error = this.t('sections.workspaces.fetch_failure');
                    this.loading = false;
                    return;
                }

                if (result.deleted) {
                    this.$emit('user:deleted', result.deleted);
                }

                this.workspaces = result.events;
            } catch (error) {
                this.error = get(error, 'response.data.error') || error.message;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Prompts the user for session invalidation
         *
         * @param {BrowserSession} session the session to kick the user from
         */
        promptKick(session) {
            const title = this.$i18n.t('general.confirm').toString();
            const text = this.t('sections.workspaces.session_actions.kick_confirm', [this.email, session.eventName]).toString();
            const okButton = this.$i18n.t('general.delete').toString();

            this.prompt(title, text, okButton, () => this.kickUser(session));
        },

        /**
         * Prompts the user for user cancellation
         *
         * @param {BrowserSession} session the session to remove the user from
         */
        promptBan(session) {
            const title = this.t('bulk.cancel').toString();
            const text = this.t('sections.workspaces.session_actions.ban_confirm', [this.email, session.eventName]).toString();
            const okButton = this.$i18n.t('general.confirm').toString();

            this.prompt(title, text, okButton, () => this.banUser(session));
        },

        /**
         * Invalidates user's session
         *
         * @param {BrowserSession} session the session to kick the user from
         */
        async kickUser(session) {
            const action = () => this.$services.userSession.invalidateInEvent(session.eid, session.pid, session.eventNodeUrl);

            await this.performAction(action, 'sections.workspaces.session_actions.kick');

            this.init();
        },

        /**
         * @param {BrowserSession} session the session object to get the URL from
         */
        async banUser(session) {
            const action = () => this.$services.userSession.cancelInEvent(session.eid, session.pid, session.eventNodeUrl);

            await this.performAction(action, 'sections.workspaces.session_actions.ban');

            this.init();
        },

        /**
         * Builds the complete URL for the user's participant profile on the event.
         *
         * @param {BrowserSession} session the session object to get the URL from
         *
         * @returns {String} the full URL to the user's profile
         */
        paxProfileUrl(session) {
            const isContentHub = session.eventType === 'content_hub';
            return this.$services.user.getProfileUrl(session.eventNodeUrl, session.eid, session.pid, isContentHub);
        },

        /**
         * Displays a detailed view for the given event sessions
         *
         * @param {BrowserSession} session the session object to get sessions details from
         */
        viewSessions(session) {
            this.$modal.show(SessionsDetails,
                {
                    node: session.eventNodeUrl,
                    eventId: session.eid,
                    userId: session.pid
                },
                {
                    classes: 'large',
                    height: 'auto',
                    reset: true
                }
            );
        },

        /**
         * Shortcut method for i18n
         *
         * @param {String} translationKey the component specific translation key
         * @param {String[]} [values] the optional interpolation values
         */
        t(translationKey, values) {
            return this.$i18n.t(`organization.pax_management.${translationKey}`, values).toString();
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('loader',{staticClass:"in-page participant-workspaces",attrs:{"status":_vm.loading}},[(!_vm.error && _vm.workspaces.length)?_c('div',{staticClass:"table-wrapper"},[_c('table',{staticClass:"table-striped"},[_c('thead',[_c('tr',[_c('th',[_vm._v(_vm._s(_vm.t('sections.workspaces.headers.workspace')))]),(_vm.showNames)?_c('th',[_vm._v(_vm._s(_vm.t('sections.workspaces.headers.full_name')))]):_vm._e(),_c('th')])]),_c('tbody',_vm._l((_vm.workspaces),function(session){return _c('tr',[_c('td',[_c('span',[_vm._v(_vm._s(session.eventName || session.eid))]),(session.fp_status === 'cancelled')?_c('div',{staticClass:"badge badge-danger"},[_vm._v(_vm._s(_vm.t('sections.workspaces.user_cancelled')))]):_vm._e()]),(_vm.showNames)?_c('td',[_vm._v(_vm._s(session.paxName))]):_vm._e(),_c('td',{staticClass:"actions"},[_c('dropdown-menu',[_c('ul',[_c('li',[_c('a',{staticClass:"has-action kick",on:{"click":function($event){return _vm.promptKick(session)}}},[_vm._v(_vm._s(_vm.t('sections.workspaces.session_actions.kick')))])]),(session.fp_status !== 'cancelled')?_c('li',[_c('a',{staticClass:"has-action ban",on:{"click":function($event){return _vm.promptBan(session)}}},[_vm._v(_vm._s(_vm.t('sections.workspaces.session_actions.ban')))])]):_vm._e(),_c('li',[_c('a',{attrs:{"href":_vm.paxProfileUrl(session)}},[_vm._v(_vm._s(_vm.t('sections.workspaces.session_actions.go_to_user')))])]),_c('li',[_c('a',{staticClass:"has-action details",on:{"click":function($event){return _vm.viewSessions(session)}}},[_vm._v(_vm._s(_vm.t('sections.workspaces.session_actions.view_all')))])])])])],1)])}),0)])]):_vm._e(),(!_vm.error && !_vm.workspaces.length)?_c('div',{staticClass:"alert alert-info"},[_c('p',[_vm._v(_vm._s(_vm.t('sections.workspaces.nothing_found')))])]):_vm._e(),(_vm.error)?_c('div',{staticClass:"alert alert-error"},[_c('h2',[_vm._v(_vm._s(_vm.$t('general.error_occurred')))]),_c('p',[_vm._v(_vm._s(_vm.error))])]):_vm._e()])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "Sessions.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__